import { InjectionToken } from '@angular/core';

/**
 * In-product guidance engine for prep-v2 (Angular 13 port of the workspace
 * `ce-tour` library). The engine knows nothing about PDFs, tags or the
 * checker: steps target **anchor ids** the app registers, and "is this step
 * done?" is a function the app supplies.
 *
 * Reactivity: the workspace library re-evaluates predicates through Angular
 * signals. This port has no signals, so predicate steps are polled on a
 * short interval outside the Angular zone, and hosts may call
 * `PrepTourService.notify()` right after a state change to advance at once.
 */

/** Consumer-defined anchor id. Branded so a step's `target` cannot silently be any string. */
export type PrepTourAnchorId = string & { readonly __prepTourAnchor?: never };
export const anchorId = (id: string): PrepTourAnchorId => id as PrepTourAnchorId;

export type PrepTourPlacement = 'top' | 'bottom' | 'left' | 'right' | 'auto';

/**
 * How a step decides it is satisfied.
 *  - `next`      the user presses Next / Finish
 *  - `click`     a click lands inside the hotspot drawn over the target
 *  - `event`     the consumer calls `PrepTourService.signal(name)`
 *  - `predicate` `satisfied()` returns true (polled while the step is shown)
 */
export type PrepTourStepAdvance =
  | { kind: 'next' }
  | { kind: 'click'; anchor?: PrepTourAnchorId }
  | { kind: 'event'; name: string }
  | { kind: 'predicate'; satisfied: () => boolean };

/** What to do when a step's anchor is not registered (element not rendered). */
export type PrepTourWhenMissing = 'skip' | 'explain' | 'abort';

/** Static text, or a function (re-read on every render tick). */
export type PrepTourText = string | (() => string);

export interface PrepTourStepPager {
  /** Zero-based position. */
  index: () => number;
  count: () => number;
  prev: () => void;
  next: () => void;
  /** Label between the arrows; default "{n} / {m}". */
  label?: () => string;
}

/** An in-card action button ("Run the checker") — the step's own way to trigger the host. */
export interface PrepTourStepAction {
  label: PrepTourText;
  /** Material icon shown before the label (may be reactive). */
  icon?: PrepTourText;
  run: () => void | Promise<void>;
  /** Hide the button (re-read on every render tick). */
  hidden?: () => boolean;
  /** Button fill (default: outline next to an enabled Next, solid otherwise). */
  fillMode?: 'solid' | 'outline' | 'flat';
}

export interface PrepTourStep {
  id: string;
  /** Registered anchor id; absent → a centred step with no spotlight. */
  target?: PrepTourAnchorId;
  placement?: PrepTourPlacement;
  /** Overrides the "Step n of m" label. */
  label?: string;
  title: PrepTourText;
  /** Plain text, already translated by the consumer. */
  body: PrepTourText;
  /** Highlighted instruction under the body ("Click … in the menu"). */
  actionNote?: PrepTourText;
  /** Default `{ kind: 'next' }`. */
  advance?: PrepTourStepAdvance;
  /** Action steps show "Skip step"; default true. */
  skippable?: boolean;
  /** Next button label override; may be reactive. */
  nextLabel?: PrepTourText;
  /** Where keyboard focus lands on enter: the card's primary control (default) or the target element. */
  focus?: 'card' | 'target';
  /** With `focus: 'target'`: focus this anchor instead of the spotlit one. */
  focusTarget?: PrepTourAnchorId;
  /**
   * Branching: the step to go to after this one (id, or a function deciding at
   * that moment). Absent → the following step; {@link PREP_TOUR_END} completes.
   */
  next?: string | (() => string | undefined);
  /** Called when Next is pressed BEFORE moving on. Return `false` to stay on the step. */
  onNext?: () => boolean | void;
  /** "Skip all": jump to this step id (or {@link PREP_TOUR_END}); a function decides at render time. */
  skipTo?: string | (() => string | undefined);
  /** Label of the skip button when `skipTo` is set. */
  skipLabel?: string;
  /** Centred, wider card with the icon above a centred title — for intro stops. */
  hero?: boolean;
  /** Material icon name. */
  icon?: string;
  /** Action button(s) rendered before Next. */
  action?: PrepTourStepAction | PrepTourStepAction[];
  /** Next / Finish button fill (default `solid`). */
  nextFillMode?: 'solid' | 'outline';
  /** Skip button fill (default `flat`). */
  skipFillMode?: 'flat' | 'outline';
  /** The card can be minimised to a small restore button; a step change restores automatically. */
  minimizable?: boolean;
  /** Previous / next arrows for a step that iterates through items. */
  pager?: PrepTourStepPager;
  /** Hide the spotlight + card while this holds — e.g. while the host is busy. */
  hideWhile?: () => boolean;
  /** `false` renders no footer buttons at all (only ×): a stop that waits for the real control. */
  buttons?: boolean;
  /** `false` hides the Back button on this stop. */
  backButton?: boolean;
  /** `false` hides the Next / Finish button on this stop. */
  nextButton?: boolean;
  /** Shows a spinner and no buttons while true; pair with a predicate advance. */
  busy?: () => boolean;
  /** Consumer hook run before the step is shown (open a panel, navigate…). */
  onEnter?: () => void | Promise<void>;
  onExit?: () => void;
  /** Default `skip`. */
  whenMissing?: PrepTourWhenMissing;
  /** Shown instead of `body` when `whenMissing === 'explain'`. */
  missingText?: string;
  /** Extra spotlight padding around the target, px (default 6). */
  padding?: number;
}

export interface PrepTourDialogContent {
  /** Material icon name. */
  icon?: string;
  title: string;
  body: string;
  /** Icon, title and buttons centred (completion cards always are). */
  centered?: boolean;
  /** × in the top-right corner; acts as the secondary (dismiss) action. */
  closeButton?: boolean;
  /** Primary button label (default from labels). */
  primary?: string;
  /** Secondary button label; omitted → no secondary button. */
  secondary?: string;
  /** Small print under the buttons. */
  footnote?: string;
  /** Run when the primary button is pressed, before the guide finishes (e.g. navigate away). */
  onPrimary?: () => void;
  /** Run when the secondary button / × is pressed, before the guide finishes. */
  onSecondary?: () => void;
}

export type PrepTourKind = 'tour' | 'recipe' | 'fixit' | 'missions';

/** How the coach-mark shows position: a dot per step, "3 of 15", or nothing. */
export type PrepTourProgressStyle = 'dots' | 'counter' | 'none';

/** `skipTo` / `next` target that ends the guide (completion) instead of jumping to a step. */
export const PREP_TOUR_END = '$end';

export interface PrepTourDefinition {
  id: string;
  kind: PrepTourKind;
  title: string;
  meta?: string;
  /** Bump to re-offer a completed guide after its content changed. */
  version?: number;
  welcome?: PrepTourDialogContent;
  /** Completion card; a function may pick one per outcome, or return null to end without a card. */
  completion?: PrepTourDialogContent | (() => PrepTourDialogContent | null);
  /** Default `dots`. */
  progress?: PrepTourProgressStyle;
  steps: PrepTourStep[];
}

export interface PrepTourProgressSnapshot {
  /** Guide ids ever started. */
  seen: string[];
  /** Guide id → version completed. */
  completed: Record<string, number>;
  /** Guide ids the user dismissed (Skip for now). */
  dismissed: string[];
  /** Guide id → last step index (resume hint). */
  lastStep: Record<string, number>;
}

/** Persistence adapter; the host backs it with whatever store it has. */
export interface PrepTourProgressAdapter {
  load(): PrepTourProgressSnapshot | null | Promise<PrepTourProgressSnapshot | null>;
  save(snapshot: PrepTourProgressSnapshot): void | Promise<void>;
  clear(): void | Promise<void>;
}

export const PREP_TOUR_PROGRESS_ADAPTER = new InjectionToken<PrepTourProgressAdapter>('PREP_TOUR_PROGRESS_ADAPTER');

export type PrepTourEventType =
  | 'start' | 'step' | 'advance' | 'back' | 'skip' | 'complete' | 'abort' | 'anchor-missing';

export interface PrepTourEvent {
  type: PrepTourEventType;
  guideId: string;
  stepId?: string;
  stepIndex?: number;
  reason?: string;
}

export type PrepTourPhase = 'welcome' | 'step' | 'done';

/** User-facing strings of the engine chrome (consumers translate). */
export interface PrepTourLabels {
  back: string;
  next: string;
  finish: string;
  skipStep: string;
  close: string;
  gotIt: string;
  start: string;
  skipForNow: string;
  done: string;
  /** "Step {n} of {m}" (card header, dots style) */
  stepOf: (n: number, m: number) => string;
  /** "{n} of {m}" (counter style) */
  counter: (n: number, m: number) => string;
  /** Screen-reader prefix for the live announcement. */
  announce: (n: number, m: number, title: string) => string;
  /** Hotspot aria-label fallback. */
  hotspot: string;
  /** Lead-in of the action note ("Your move:"). */
  yourMove: string;
  /** Spinner text of a busy stop. */
  working: string;
  missing: string;
  minimize: string;
  restore: string;
  previous: string;
  nextItem: string;
}

export const PREP_TOUR_DEFAULT_LABELS: PrepTourLabels = {
  back: 'Back',
  next: 'Next',
  finish: 'Finish',
  skipStep: 'Skip step',
  close: 'Exit guide',
  gotIt: 'Got it',
  start: 'Start',
  skipForNow: 'Skip for now',
  done: 'Done',
  stepOf: (n, m) => `Step ${n} of ${m}`,
  counter: (n, m) => `${n} of ${m}`,
  announce: (n, m, title) => `Step ${n} of ${m}: ${title}`,
  hotspot: 'Click the highlighted area to continue',
  yourMove: 'Your move:',
  working: 'Working…',
  missing: 'This part of the screen is not available right now.',
  minimize: 'Minimise',
  restore: 'Show the step',
  previous: 'Previous',
  nextItem: 'Next',
};

export const PREP_TOUR_LABELS = new InjectionToken<PrepTourLabels>('PREP_TOUR_LABELS', {
  providedIn: 'root',
  factory: () => PREP_TOUR_DEFAULT_LABELS,
});

/** Resolve a static-or-reactive text. */
export const prepTourText = (v: PrepTourText | undefined): string => (typeof v === 'function' ? v() : (v || ''));
